import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { RefreshCw, AlertCircle, Clock, CheckCircle, XCircle, Phone, Mail } from 'lucide-react';

const CancellationRefund = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-900 to-black py-12 sm:py-16">
          <div className="container mx-auto px-4 text-center">
            <RefreshCw className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Cancellation & <span className="text-yellow-500">Refund</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Transparent policies for hassle-free returns and refunds
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
            
            {/* Last Updated */}
            <p className="text-gray-500 text-sm mb-8">Last Updated: January 2025</p>

            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                At <strong>Jain Watch Company</strong>, customer satisfaction is our priority. We understand that sometimes a purchase may not meet your expectations. This policy explains how you can cancel orders, request returns, or obtain refunds for products purchased from our website.
              </p>
            </section>

            {/* Order Cancellation */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <XCircle className="w-6 h-6 text-red-500 mr-2" />
                Order Cancellation
              </h2>
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-bold text-green-700 mb-2">Before Dispatch</h3>
                  <p className="text-gray-700">
                    You may cancel your order <strong>free of charge</strong> if it has not yet been dispatched. Contact us immediately via phone or email with your order details.
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-bold text-yellow-700 mb-2">After Dispatch</h3>
                  <p className="text-gray-700">
                    Once the order is dispatched, cancellation is <strong>not possible</strong>. You may refuse delivery or initiate a return after receiving the product.
                  </p>
                </div>
              </div>
            </section>

            {/* Return Policy */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <RefreshCw className="w-6 h-6 text-yellow-500 mr-2" />
                Return Policy
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4">
                <h3 className="font-bold text-gray-900 mb-3">Return Window</h3>
                <p className="text-gray-700 mb-4">
                  We accept returns within <strong>7 days</strong> of delivery, provided the product meets our return conditions.
                </p>
                
                <h3 className="font-bold text-gray-900 mb-3">Conditions for Return</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    Product must be unused, unworn, and in its original condition
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    Original packaging, tags, warranty card, and accessories must be intact
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    Proof of purchase (order confirmation email or invoice) is required
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    Product should not have any scratches, damages, or signs of use
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
                <h3 className="font-bold text-red-700 mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Non-Returnable Items
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Watches with removed or altered protective films/stickers</li>
                  <li>• Products damaged due to customer mishandling</li>
                  <li>• Items purchased during special sales or with discount coupons (unless defective)</li>
                  <li>• Customized or personalized products</li>
                </ul>
              </div>
            </section>

            {/* How to Return */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">How to Initiate a Return</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 font-bold">1</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Contact Us</h3>
                    <p className="text-gray-700">Email us at jainwatchcompany1975@gmail.com or call +91 9927058909 with your order number and reason for return.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 font-bold">2</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Receive Approval</h3>
                    <p className="text-gray-700">Our team will review your request and provide return authorization within 24-48 hours.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 font-bold">3</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Ship the Product</h3>
                    <p className="text-gray-700">Pack the product securely in its original packaging and ship it to our address. You will bear the return shipping cost unless the product is defective or wrong.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="bg-yellow-500 text-black rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 font-bold">4</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Quality Check</h3>
                    <p className="text-gray-700">Once we receive the product, our team will inspect it within 3-5 business days.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Refund Policy */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="w-6 h-6 text-yellow-500 mr-2" />
                Refund Policy
              </h2>
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-3 text-left text-sm font-semibold">Scenario</th>
                      <th className="p-3 text-left text-sm font-semibold">Refund Timeline</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b">
                      <td className="p-3">Order cancelled before dispatch</td>
                      <td className="p-3 font-semibold text-green-600">5-7 Business Days</td>
                    </tr>
                    <tr className="border-b bg-gray-50">
                      <td className="p-3">Return approved after inspection</td>
                      <td className="p-3 font-semibold text-green-600">7-10 Business Days</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3">Payment failed but amount debited</td>
                      <td className="p-3 font-semibold text-green-600">5-7 Business Days (auto-reversal)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-bold text-blue-700 mb-2">Refund Method</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Refunds will be processed to the <strong>original payment method</strong> used during purchase.</li>
                  <li>• For UPI/Net Banking: Refund credited to the source account.</li>
                  <li>• For Credit/Debit Cards: Refund reflects in 7-10 business days depending on your bank.</li>
                </ul>
              </div>
            </section>

            {/* Defective Products */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Defective or Damaged Products</h2>
              <p className="text-gray-700 mb-4">
                If you receive a defective, damaged, or incorrect product:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Contact us within <strong>48 hours</strong> of delivery with photos/videos of the defect.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  We will arrange a <strong>free pickup</strong> and provide a replacement or full refund.
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  No return shipping charges will apply for genuinely defective products.
                </li>
              </ul>
            </section>

            {/* Exchange */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Exchange Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We currently do not offer direct exchanges. If you wish to exchange a product, please initiate a return and place a new order for the desired item.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gray-900 text-white rounded-lg p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Need Assistance?</h2>
              <p className="text-gray-300 mb-4">For returns, refunds, or any queries, reach out to us:</p>
              <div className="space-y-3">
                <p className="text-white font-semibold">Jain Watch Company</p>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="mailto:jainwatchcompany1975@gmail.com" className="text-yellow-500 hover:underline">
                    jainwatchcompany1975@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-yellow-500 mr-3" />
                  <a href="tel:9927058909" className="text-yellow-500 hover:underline">
                    +91 9927058909
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CancellationRefund;
